import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import PageContextHeader from './PageContextHeader';
import { useLoading } from '../contexts/LoadingContext';
import { useSidebar } from '../contexts/SidebarContext';

interface AppLayoutProps {
  children: React.ReactNode;
}

/**
 * Shell stays mounted during page fetches. The old full-screen `loading`
 * branch tore down Header/Sidebar whenever Edit Athlete (and similar) called
 * LoadingContext.setLoading — same “stuck context” feel as web nav without
 * route loading UI. Progress is content-scoped only.
 */
const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { loading } = useLoading();
  const { mobileOpen, setMobileOpen } = useSidebar();
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.innerWidth < 768) {
      scrollRef.current?.scrollTo({ top: 0 });
    }
  }, [location.pathname]);

  return (
    <div className="relative flex h-screen overflow-hidden bg-background transition-none">
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-[2px] md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="absolute inset-x-0 top-0 z-50">
        <Header />
        {loading ? (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 overflow-hidden"
            aria-hidden
          >
            <div className="h-full w-1/3 animate-pulse bg-accent-primary dark:bg-[#D9F42B]" />
          </div>
        ) : null}
      </div>

      <Sidebar />

      <div
        ref={scrollRef}
        className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden bg-background pt-14 md:pt-[4.5rem] transition-none"
      >
        <div className="relative flex min-h-full w-full min-w-0 max-w-full flex-col px-4 pt-3 pb-3 sm:px-6 lg:px-8">
          <PageContextHeader />
          <div
            className={`relative flex grow flex-col ${loading ? 'opacity-60' : ''}`}
          >
            {children}
            {loading ? (
              <div
                className="pointer-events-none absolute inset-0 flex items-start justify-center pt-24"
                aria-busy="true"
                aria-label="Loading"
              >
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent-primary/30 border-t-accent-primary dark:border-[#D9F42B]/30 dark:border-t-[#D9F42B]" />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
